import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, CssBaseline, CircularProgress, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { DataProvider } from './context/DataContext';
import { AuthProvider } from './context/AuthContext';
import theme from './theme';
// Registers Chart.js components and global chart defaults once for the app.
import './theme/charts';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import Analytics from './pages/Analytics';
import Allocation from './pages/Allocation';
import Categorisation from './pages/Categorisation';
import Settings from './pages/Settings';
import Help from './pages/Help';
import NotFound from './pages/NotFound';
import Admin from './pages/Admin';
import Login from './pages/Login';
import DevLogin from './pages/DevLogin';
import Timer from './pages/Timer';
import { markExplicitSignOut } from './utils/autoLogin';

const isDev = process.env.NODE_ENV === 'development';
const skipAuth = isDev && process.env.REACT_APP_SKIP_AUTH === 'true';
const useEntraInDev = isDev && process.env.REACT_APP_USE_ENTRA_IN_DEV === 'true';

/** Clears client state then bounces to the login screen. */
const Logout = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Mark the sign-out as deliberate so the login screen does NOT immediately
    // auto-redirect back into Entra SSO and sign the user straight back in.
    markExplicitSignOut();
    const timer = setTimeout(() => navigate('/login', { replace: true }), 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress size={32} />
      <Typography variant="body2" color="text.secondary">
        Signing out…
      </Typography>
    </Box>
  );
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <DataProvider>
              <Routes>
                <Route
                  path="/"
                  element={
                    skipAuth && !useEntraInDev ? (
                      <Navigate to="/dev-login" replace />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {skipAuth && !useEntraInDev ? (
                  <Route path="/dev-login" element={<DevLogin />} />
                ) : (
                  <Route path="/login" element={<Login />} />
                )}

                <Route path="/logout" element={<Logout />} />

                <Route element={<ProtectedRoute />}>
                  <Route element={<Layout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/tickets" element={<Tickets />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/allocation" element={<Allocation />} />
                    <Route path="/categorisation" element={<Categorisation />} />
                    <Route path="/timer" element={<Timer />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/admin" element={<Admin />} />
                  </Route>
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </DataProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
